<template>
  <div class="nap-overlay" @click.self="$emit('close')">
    <div class="card nap-card" role="dialog" aria-label="Novo agendamento">
      <div class="card-head">
        <h3>Novo agendamento</h3>
        <button class="nap-x" aria-label="Fechar" @click="$emit('close')">
          ✕
        </button>
      </div>

      <div class="field nap-psearch">
        <label class="label">Paciente</label>
        <input
          v-model="patientQuery"
          class="input"
          placeholder="Buscar por nome, CPF ou telefone…"
          @input="onPatientInput"
          @focus="patientListOpen = true"
        />
        <div v-if="patientListOpen && visiblePatients.length" class="nap-plist">
          <button
            v-for="p in visiblePatients"
            :key="p.id"
            type="button"
            class="nap-pitem"
            @click="pickPatient(p)"
          >
            <b>{{ p.name }}</b>
            <span v-if="p.contact">{{ formatPhone(p.contact) }}</span>
          </button>
        </div>
        <span v-if="form.patientId" class="nap-picked">
          ✓ {{ patientQuery }}
        </span>
      </div>

      <div class="field">
        <label class="label">Profissional de saúde</label>
        <select v-model="form.doctorId" class="select" @change="onDoctorChange">
          <option value="" disabled>Selecione o profissional de saúde…</option>
          <option v-for="d in doctors" :key="d.id" :value="d.id">
            {{ d.name }}
          </option>
        </select>
      </div>

      <div class="nap-grid">
        <div class="field">
          <label class="label">Data</label>
          <select
            v-model="form.date"
            class="select"
            :disabled="!dateOptions.length"
            @change="form.time = ''"
          >
            <option value="" disabled>
              {{ datePlaceholder }}
            </option>
            <option v-for="d in dateOptions" :key="d" :value="d">
              {{ fmtDate(d) }}
            </option>
          </select>
        </div>
        <div class="field">
          <label class="label">Horário</label>
          <select v-model="form.time" class="select" :disabled="!form.date">
            <option value="" disabled>Selecione…</option>
            <option v-for="t in timeOptions" :key="t" :value="t">
              {{ t }}
            </option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="label">
          Observação
          <span class="nap-opt">(opcional)</span>
        </label>
        <textarea
          v-model="form.observation"
          class="textarea"
          rows="2"
          placeholder="Ex.: retorno, encaixe, pedido do paciente…"
        />
      </div>

      <div class="nap-actions">
        <button class="btn btn--outline btn--sm" @click="$emit('close')">
          Cancelar
        </button>
        <button
          class="btn btn--primary btn--sm"
          :disabled="submitting"
          @click="submit"
        >
          {{ submitting ? "Agendando…" : "Agendar consulta" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Modal de novo agendamento pela clínica: staff escolhe paciente + médico +
// horário disponível (slots reais) e cria a consulta em nome do paciente via
// POST /admin/appointments (order 'paid' zerada; notificações no backend).
import {
  ClinicPatients,
  ClinicDoctors,
  ClinicAgenda
} from "@/services/clinica";
import { formatPhone } from "@/utils/formatPhone";

export default {
  name: "ClinicNewAppointment",
  data() {
    return {
      patients: [],
      doctors: [],
      // busca de paciente (server-side ?q=) — resultados e estado do dropdown
      patientQuery: "",
      patientResults: null,
      patientListOpen: false,
      // dias disponíveis do médico selecionado: [{id, slot_date, slots:[]}]
      slotDays: [],
      loadingSlots: false,
      submitting: false,
      form: {
        patientId: "",
        doctorId: "",
        date: "",
        time: "",
        observation: ""
      }
    };
  },
  created() {
    this.loadOptions();
  },
  methods: {
    formatPhone,
    // Digitar invalida a seleção anterior e agenda a busca (debounce).
    onPatientInput() {
      this.form.patientId = "";
      this.patientListOpen = true;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.searchPatients(), 250);
    },
    async searchPatients() {
      const q = this.patientQuery.trim();
      if (q.length < 2) {
        this.patientResults = null; // volta à lista inicial
        return;
      }
      try {
        const { data } = await ClinicPatients.list({ q, per: 20 });
        this.patientResults = data.items || [];
      } catch (e) {
        this.patientResults = [];
      }
    },
    pickPatient(p) {
      this.form.patientId = p.id;
      this.patientQuery = p.name;
      this.patientListOpen = false;
    },
    async loadOptions() {
      try {
        const [pat, doc] = await Promise.all([
          ClinicPatients.list({ per: 100 }),
          ClinicDoctors.list()
        ]);
        this.patients = pat.data.items || [];
        this.doctors = doc.data || [];
      } catch (e) {
        this.$toast.error(
          "Não foi possível carregar pacientes/profissionais de saúde"
        );
      }
    },
    async onDoctorChange() {
      this.form.date = "";
      this.form.time = "";
      this.slotDays = [];
      if (!this.form.doctorId) return;
      this.loadingSlots = true;
      try {
        const { data } = await ClinicAgenda.doctorSlots(this.form.doctorId);
        this.slotDays = data || [];
      } catch (e) {
        this.$toast.error(
          "Não foi possível carregar os horários do profissional de saúde"
        );
      } finally {
        this.loadingSlots = false;
      }
    },
    isFuture(date, time) {
      return new Date(`${date}T${time}:00`) > new Date();
    },
    fmtDate(iso) {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },
    async submit() {
      const f = this.form;
      if (!f.patientId || !f.doctorId || !f.date || !f.time) {
        this.$toast.error(
          "Preencha paciente, profissional de saúde, data e horário"
        );
        return;
      }
      const match = (this.groupedDays[f.date] || []).find(
        t => t.hour === f.time
      );
      if (!match) {
        this.$toast.error("Horário indisponível — escolha outro");
        return;
      }
      this.submitting = true;
      try {
        const { data } = await ClinicAgenda.createAppointment({
          patientId: f.patientId,
          slotId: match.slotId,
          scheduledAt: `${f.date}T${f.time}:00`,
          observation: f.observation || undefined
        });
        this.$toast.success("Consulta agendada");
        this.$emit("created", data);
      } catch (e) {
        const status = e && e.response && e.response.status;
        const msg =
          status === 422
            ? "Horário já ocupado — escolha outro"
            : status === 403
            ? "Você não tem permissão para agendar neste horário da clínica."
            : "Não foi possível agendar. Tente novamente.";
        this.$toast.error(msg);
      } finally {
        this.submitting = false;
      }
    }
  },
  computed: {
    // Resultados da busca (se houver query ≥2 chars) ou lista inicial; o
    // dropdown mostra no máximo 8 p/ não virar um scroll gigante.
    visiblePatients() {
      const base =
        this.patientResults !== null ? this.patientResults : this.patients;
      return base.slice(0, 8);
    },
    // agrupa os blocos de Slot por slot_date (pode haver mais de um Slot no
    // mesmo dia — ex. duplicado por reenvio de "Criar agenda") guardando de
    // qual slot_id cada horário veio, igual TimeTable.vue faz pro paciente.
    groupedDays() {
      return this.slotDays.reduce((acc, day) => {
        const times = (day.slots || [])
          .filter(t => this.isFuture(day.slot_date, t))
          .map(hour => ({ hour, slotId: day.id }));
        if (times.length) {
          acc[day.slot_date] = [...(acc[day.slot_date] || []), ...times];
        }
        return acc;
      }, {});
    },
    dateOptions() {
      return Object.keys(this.groupedDays);
    },
    timeOptions() {
      const times = this.groupedDays[this.form.date] || [];
      const seen = new Set();
      return times
        .map(t => t.hour)
        .filter(hour => (seen.has(hour) ? false : seen.add(hour) || true));
    },
    datePlaceholder() {
      if (!this.form.doctorId)
        return "Escolha o profissional de saúde primeiro";
      if (this.loadingSlots) return "Carregando horários…";
      if (!this.dateOptions.length) return "Sem horários disponíveis";
      return "Selecione…";
    }
  }
};
</script>

<style lang="scss" scoped>
.nap-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
}
.nap-card {
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.nap-x {
  border: none;
  background: none;
  color: var(--color-gray-400);
  font-size: var(--font-size-md);
  cursor: pointer;
  &:hover {
    color: var(--color-gray-700);
  }
}
.nap-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}
.nap-psearch {
  position: relative;
}
.nap-plist {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  background: #fff;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  max-height: 240px;
  overflow: auto;
}
.nap-pitem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  font-size: var(--font-size-sm);

  b {
    color: var(--color-gray-900);
  }
  span {
    color: var(--color-gray-400);
    font-size: var(--font-size-xs);
    white-space: nowrap;
  }
  &:hover {
    background: var(--color-gray-50);
  }
}
.nap-picked {
  font-size: var(--font-size-xs);
  color: var(--color-success);
  font-weight: var(--fw-semibold);
}
.nap-opt {
  color: var(--color-gray-400);
  font-weight: var(--fw-regular);
}
.nap-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}
</style>
